import React, { useState, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { ComisionesTable } from './ComisionesTable';
import { ProductosABC } from './ProductosABC';
import { DashboardRepresentante } from './DashboardRepresentante';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from "@/components/auth/AuthContext";
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ListadoPedidos } from "./ListadoPedidos";
import { isCancelledOrderStatus } from "@/lib/orderStatus";

export function Informes() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPedidos: 0,
    pedidosPendientes: 0,
    pedidosCompletados: 0,
    totalUnidades: 0
  });
  const [mostrarListadoPedidos, setMostrarListadoPedidos] = useState(false);

  useEffect(() => {
    cargarEstadisticas();
  }, []);

  const cargarEstadisticas = async () => {
    try {
      setLoading(true);
      
      // Construir la consulta base para pedidos
      let query = supabase
        .from('orders')
        .select(`
          id,
          status,
          cod_rep,
          total,
          items:order_items(
            quantity,
            pending_quantity
          )
        `);

      // Si el usuario no tiene permiso para ver comisiones globales, filtrar por su cod_rep
      if (!user.ver_comisiones_globales) {
        query = query.eq('cod_rep', user.cod_rep);
      }

      const { data: pedidosData, error: pedidosError } = await query;

      if (pedidosError) throw pedidosError;

      const activePedidosData = (pedidosData || []).filter((pedido) => !isCancelledOrderStatus(pedido.status));
      const totalPedidos = activePedidosData.length;
      // Pendientes por líneas: cuenta pedidos que tienen al menos UNA línea con pending_quantity > 0
      const pedidosPendientes = activePedidosData.filter(p => (p.items || []).some(it => (it.pending_quantity || 0) > 0)).length;
      const pedidosCompletados = activePedidosData.filter(p => (p.items || []).every(it => (it.pending_quantity || 0) === 0) && p.status === 'completed').length;
      
      // Calcular total de unidades pendientes (suma de pending_quantity)
      const totalUnidades = activePedidosData.reduce((acc, pedido) => {
        const unidadesPendientes = pedido.items?.reduce((sum, item) => sum + (item.pending_quantity || 0), 0) || 0;
        return acc + unidadesPendientes;
      }, 0);

      // Calcular importe total de ventas del periodo (suma de order.total)
      const totalVentas = activePedidosData.reduce((acc, pedido) => acc + (pedido.total || 0), 0);

      setStats({
        totalPedidos,
        pedidosPendientes,
        pedidosCompletados,
        totalUnidades,
        totalVentas
      });

    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVolver = () => {
    window.location.hash = '#/';
  };

  if (loading) {
    return <div className="p-4">Cargando estadísticas...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          onClick={handleVolver}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        <h1 className="text-2xl font-bold">Informes y Estadísticas</h1>
      </div>
      
      <TooltipProvider>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="p-4">
          <div className="text-lg font-semibold mb-2 flex items-center gap-1">
            {user.ver_comisiones_globales ? 'Total de Pedidos' : 'Mis Pedidos'}
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>Número de pedidos en el período seleccionado</TooltipContent>
            </Tooltip>
          </div>
          <p className="text-3xl font-bold">{stats.totalPedidos}</p>
        </Card>
        
        <Card className="p-4">
          <div className="text-lg font-semibold mb-2 flex items-center gap-1">
            {user.ver_comisiones_globales ? 'Pedidos Pendientes' : 'Mis Pedidos Pendientes'}
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>Pedidos con al menos una línea con pending_quantity &gt; 0</TooltipContent>
            </Tooltip>
          </div>
          <p className="text-3xl font-bold text-yellow-600">{stats.pedidosPendientes}</p>
        </Card>
        
        <Card className="p-4">
          <div className="text-lg font-semibold mb-2 flex items-center gap-1">
            {user.ver_comisiones_globales ? 'Pedidos Completados' : 'Mis Pedidos Completados'}
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>Todos los items con pendiente = 0 y estado del pedido completed</TooltipContent>
            </Tooltip>
          </div>
          <p className="text-3xl font-bold text-green-600">{stats.pedidosCompletados}</p>
        </Card>
        
        <Card className="p-4">
          <div className="text-lg font-semibold mb-2 flex items-center gap-1">
            {user.ver_comisiones_globales ? 'Unidades Pendientes' : 'Mis Unidades Pendientes'}
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>Suma de pending_quantity de las líneas (según permisos/cod_rep)</TooltipContent>
            </Tooltip>
          </div>
          <p className="text-3xl font-bold text-blue-600">
            {stats.totalUnidades.toLocaleString('es-ES')}
          </p>
        </Card>
      </div>
      </TooltipProvider>

      <div className="flex flex-wrap gap-3 mb-8">
        <Button onClick={() => setMostrarListadoPedidos(true)}>
          Listado de pedidos
        </Button>
      </div>

      <div className="space-y-8">
        <h2 className="text-xl font-bold mb-4">Comisiones de Vendedores</h2>
        <ComisionesTable />

        <h2 className="text-xl font-bold mb-4">Análisis de Productos</h2>
        <ProductosABC />
      </div>

      <Dialog open={mostrarListadoPedidos} onOpenChange={setMostrarListadoPedidos}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Listado de pedidos</DialogTitle>
          </DialogHeader>
          <ListadoPedidos />
        </DialogContent>
      </Dialog>
    </div>
  );
}
